import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'

const isComingSoon = import.meta.env.VITE_COMING_SOON === 'true'

const comingSoonRoutes: RouteRecordRaw[] = [
  {
    path: '/:pathMatch(.*)*',
    name: 'coming-soon',
    component: () => import('@/views/ComingSoonView.vue'),
  },
]

const appRoutes: RouteRecordRaw[] = [
  // --- Auth (public) ---
  {
    path: '/login',
    name: 'login',
    component: () => import('@/views/auth/LoginView.vue'),
    meta: { guest: true },
  },
  {
    path: '/register',
    name: 'register',
    component: () => import('@/views/auth/RegisterView.vue'),
    meta: { guest: true },
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/auth/ForgotPasswordView.vue'),
    meta: { guest: true },
  },

  // --- OAuth Callback ---
  {
    path: '/auth/callback',
    name: 'auth-callback',
    component: () => import('@/views/auth/OAuthCallbackView.vue'),
  },

  // --- Onboarding ---
  {
    path: '/onboarding',
    name: 'onboarding',
    component: () => import('@/views/onboarding/OnboardingView.vue'),
    meta: { requiresAuth: true },
  },

  // --- Invitation Accept ---
  {
    path: '/invitations/:token/accept',
    name: 'invitation-accept',
    component: () => import('@/views/auth/InvitationAcceptView.vue'),
  },

  // --- Organization-scoped routes ---
  {
    path: '/org/:slug',
    meta: { requiresAuth: true },
    children: [
      // Dashboard
      {
        path: '',
        redirect: { name: 'dashboard' },
      },
      {
        path: 'dashboard',
        name: 'dashboard',
        component: () => import('@/views/dashboard/DashboardView.vue'),
      },

      // Repositories
      {
        path: 'repositories',
        name: 'repositories',
        component: () => import('@/views/repositories/RepositoryListView.vue'),
      },
      {
        path: 'repositories/:repoId',
        name: 'repository-detail',
        component: () => import('@/views/repositories/RepositoryDetailView.vue'),
      },

      // Findings
      {
        path: 'findings',
        name: 'findings',
        component: () => import('@/views/findings/FindingsListView.vue'),
      },
      {
        path: 'findings/:findingId',
        name: 'finding-detail',
        component: () => import('@/views/findings/FindingDetailView.vue'),
      },

      // Settings
      {
        path: 'settings',
        component: () => import('@/views/settings/SettingsLayout.vue'),
        children: [
          {
            path: '',
            redirect: { name: 'settings-members' },
          },
          {
            path: 'members',
            name: 'settings-members',
            component: () => import('@/views/settings/MembersView.vue'),
          },
          {
            path: 'billing',
            name: 'settings-billing',
            component: () => import('@/views/settings/BillingView.vue'),
          },
          {
            path: 'git-connections',
            name: 'settings-git',
            component: () => import('@/views/settings/GitConnectionsView.vue'),
          },
          {
            path: 'general',
            name: 'settings-general',
            component: () => import('@/views/settings/GeneralView.vue'),
          },
          {
            path: 'profile',
            name: 'settings-profile',
            component: () => import('@/views/settings/ProfileView.vue'),
          },
        ],
      },
    ],
  },

  // --- User Profile (standalone, redirects to settings) ---
  {
    path: '/profile',
    redirect: '/login',
  },

  // --- Catch-all redirect ---
  {
    path: '/:pathMatch(.*)*',
    redirect: '/login',
  },
]

const routes = isComingSoon ? comingSoonRoutes : appRoutes

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    if (savedPosition) return savedPosition
    return { top: 0 }
  },
})

// Navigation guards
router.beforeEach(async (to) => {
  if (isComingSoon) return
  const authStore = useAuthStore()

  // Check if user is authenticated
  if (!authStore.isInitialized) {
    await authStore.initialize()
  }

  // Persist org slug from URL into store (e.g. after page refresh)
  if (to.params.slug && typeof to.params.slug === 'string') {
    authStore.setCurrentOrganization(to.params.slug)
  }

  // Redirect authenticated users away from guest pages
  if (to.meta.guest && authStore.isAuthenticated) {
    const slug = authStore.currentOrganizationSlug
    if (slug) {
      return { name: 'dashboard', params: { slug } }
    }
    return { name: 'onboarding' }
  }

  // Redirect unauthenticated users to login
  if (to.meta.requiresAuth && !authStore.isAuthenticated) {
    return { name: 'login', query: { redirect: to.fullPath } }
  }
})

export default router
